import React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";

const Collapsible = React.forwardRef(({ className = "", ...props }, ref) => {
  const rootClasses = ["menu-collapsible-root", className].filter(Boolean).join(" ");

  return <CollapsiblePrimitive.Root ref={ref} className={rootClasses} {...props} />;
});

Collapsible.displayName = "Collapsible";

const CollapsibleTrigger = React.forwardRef(
  (
    {
      children,
      className = "",
      quiet = false,
      hideChevron = false,
      actions = null,
      actionsClassName = "",
      ...props
    },
    ref
  ) => {
    const triggerClasses = [
      "menu-collapsible-trigger",
      quiet ? "menu-collapsible-trigger-quiet" : "",
      actions != null ? "menu-collapsible-trigger-with-actions" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    if (actions != null) {
      const actionsClasses = ["menu-collapsible-trigger-actions", actionsClassName]
        .filter(Boolean)
        .join(" ");
      const stopActionToggle = (event) => {
        event.stopPropagation();
      };

      return (
        <div
          className={triggerClasses}
          style={{
            backgroundColor: "transparent",
            outline: "none",
            border: "none",
          }}
        >
          <CollapsiblePrimitive.Trigger
            ref={ref}
            className="menu-collapsible-trigger-button"
            {...props}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
          </CollapsiblePrimitive.Trigger>
          <div
            className={actionsClasses}
            onPointerDown={stopActionToggle}
            onClick={stopActionToggle}
          >
            {actions}
          </div>
          {!hideChevron ? (
            <CollapsiblePrimitive.Trigger
              className="menu-collapsible-trigger-chevron-button"
              aria-label={props["aria-label"] || "Toggle section"}
              type="button"
            >
              <svg
                className="menu-collapsible-chevron shrink-0 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </CollapsiblePrimitive.Trigger>
          ) : null}
        </div>
      );
    }

    return (
      <CollapsiblePrimitive.Trigger
        ref={ref}
        className={triggerClasses}
        style={{
          backgroundColor: "transparent",
          outline: "none",
          border: "none",
        }}
        {...props}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
        {!hideChevron ? (
          <svg
            className="menu-collapsible-chevron shrink-0 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        ) : null}
      </CollapsiblePrimitive.Trigger>
    );
  }
);

CollapsibleTrigger.displayName = "CollapsibleTrigger";

const CollapsibleContent = React.forwardRef(
  ({ children, className = "", unmountOnClose = false, ...props }, ref) => {
    const contentRef = React.useRef(null);
    const lastStateRef = React.useRef(null);
    const [hasToggled, setHasToggled] = React.useState(false);
    const contentClassName = [
      "menu-collapsible-content",
      hasToggled ? "menu-collapsible-content-has-toggled" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    React.useImperativeHandle(ref, () => contentRef.current);

    React.useEffect(() => {
      const node = contentRef.current;
      if (!node) return undefined;

      lastStateRef.current = node.getAttribute("data-state");
      if (!window.MutationObserver) return undefined;

      const observer = new window.MutationObserver(() => {
        const nextState = node.getAttribute("data-state");
        if (lastStateRef.current != null && nextState !== lastStateRef.current) {
          setHasToggled(true);
        }
        lastStateRef.current = nextState;
      });

      observer.observe(node, { attributes: true, attributeFilter: ["data-state"] });
      return () => observer.disconnect();
    }, []);

    return (
      <CollapsiblePrimitive.Content
        ref={contentRef}
        forceMount={!unmountOnClose}
        className={contentClassName}
        {...props}
      >
        <div className="menu-collapsible-content-inner pt-1 pb-1">{children}</div>
      </CollapsiblePrimitive.Content>
    );
  }
);

CollapsibleContent.displayName = "CollapsibleContent";

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
